import { CHANNELS } from "../../shared/channels";
import { StudyDeck, StudyGrade, StudySnapshot } from "../../shared/types";
import { LANGUAGES, deckInfo, languageOfDeck } from "../../shared/languages";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast } from "../dom";
import { isViewActive, onViewHidden, onViewShown } from "../navigation";
import { buzz } from "../tweens";
import { t } from "../i18n";
import { setNeedle } from "./today_view";

// anki style flashcards. the service owns the queue, this only shows the card it hands over and sends the grade back

const GRADES: StudyGrade[] = ["again", "hard", "good", "easy"];

let shown: StudySnapshot | null = null;
let revealed = false;
let busy = false;
let waitTimer = 0;

function paintCounts(snapshot: StudySnapshot): void {
  byId("jp-count-fresh").textContent = String(snapshot.counts.fresh);
  byId("jp-count-learning").textContent = String(snapshot.counts.learning);
  byId("jp-count-review").textContent = String(snapshot.counts.review);
  setNeedle("jp-count-fresh", snapshot.counts.fresh / Math.max(1, snapshot.settings.newPerDay));
  setNeedle("jp-count-learning", snapshot.counts.learning / 15);
  setNeedle("jp-count-review", snapshot.counts.review / 60);
  const { today, streak } = snapshot;
  const parts = [today.reviewed ? t("{n} answered today", { n: today.reviewed }) : "", streak ? t("{n} day streak", { n: streak }) : ""];
  byId("jp-streak").textContent = parts.filter(Boolean).join(" · ");
}

function paintCard(snapshot: StudySnapshot): void {
  const card = snapshot.card;
  byId("jp-study").hidden = !card;
  byId("jp-done").hidden = Boolean(card);
  if (!card) {
    const waiting = snapshot.waitingUntil ? Math.max(1, Math.round((snapshot.waitingUntil - Date.now()) / 60_000)) : 0;
    byId("jp-done").textContent = waiting
      ? t("nothing due right now, {n} learning cards come back in {minutes}m", { n: snapshot.counts.learning, minutes: waiting })
      : snapshot.today.done ? t("today's cards are done, see you tomorrow") : t("no cards to study, switch a deck on below");
    window.clearTimeout(waitTimer);
    if (waiting) waitTimer = window.setTimeout(() => void refresh(), Math.min(waiting * 60_000, 20 * 60_000));
    return;
  }
  revealed = false;
  byId("jp-detail").textContent = card.fresh ? `${t("new")} · ${card.detail}` : card.detail;
  byId("jp-front").textContent = card.front;
  const language = LANGUAGES.find((item) => item.id === languageOfDeck(card.deck));
  byId("jp-front").setAttribute("lang", language?.tag || "ja");
  byId("jp-readings").setAttribute("lang", language?.tag || "ja");
  byId("jp-front").classList.toggle("is-kanji", card.deck === "kanji" || card.deck === "zh-words");
  byId("jp-answer").textContent = card.answer;
  byId("jp-readings").textContent = card.readings;
  byId("jp-back").hidden = true;
  byId("jp-reveal").hidden = false;
  byId("jp-grades").hidden = true;
  for (const grade of GRADES) byId("jp-grades").querySelector(`[data-next="${grade}"]`)!.textContent = card.next[grade];
}

function paintDecks(snapshot: StudySnapshot): void {
  byId("jp-new-per-day").querySelectorAll<HTMLButtonElement>(".seg-option").forEach((option) => {
    option.classList.toggle("active", Number(option.dataset.value) === snapshot.settings.newPerDay);
  });
  // one language at a time: the chips pick it, the decks below are only that language's
  const current = snapshot.settings.language;
  byId("jp-langs").replaceChildren(...LANGUAGES.map((language) => {
    const info = snapshot.languages.find((item) => item.id === language.id);
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = `chip${language.id === current ? " active" : ""}`;
    chip.lang = language.tag;
    chip.setAttribute("role", "tab");
    chip.setAttribute("aria-selected", String(language.id === current));
    chip.textContent = language.native;
    if (info?.due) {
      const badge = document.createElement("b");
      badge.className = "chip-badge";
      badge.textContent = String(info.due);
      chip.append(badge);
    }
    chip.addEventListener("click", () => {
      if (language.id !== current) void saveSettings({ language: language.id });
    });
    return chip;
  }));
  const rows = snapshot.decks.filter((deck) => languageOfDeck(deck.deck) === current).map((deck) => {
    const row = fillFields(cloneTemplate("template-study-deck"), {
      name: t(deckInfo(deck.deck)?.label || deck.deck),
      meta: t("{seen} of {total} seen · {solid} solid", { seen: deck.seen, total: deck.total, solid: deck.mature })
    });
    const box = row.querySelector<HTMLInputElement>("input")!;
    box.checked = snapshot.settings.decks.includes(deck.deck);
    box.setAttribute("aria-label", t("study {deck}", { deck: t(deckInfo(deck.deck)?.label || deck.deck) }));
    box.addEventListener("change", () => void toggleDeck(deck.deck, box.checked));
    row.querySelector<HTMLElement>(".jp-deck-bar > i")!.style.transform = `scaleX(${deck.total ? deck.seen / deck.total : 0})`;
    return row;
  });
  byId("jp-decks").replaceChildren(...rows);
}

function paint(snapshot: StudySnapshot): void {
  const sameCard = shown?.card?.id === snapshot.card?.id && revealed;
  shown = snapshot;
  paintCounts(snapshot);
  paintDecks(snapshot);
  // a live update from another device should not flip a card that is already showing its answer
  if (!sameCard) paintCard(snapshot);
}

async function refresh(): Promise<void> {
  try {
    paint(await call<StudySnapshot>(CHANNELS.japaneseToday));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load the deck"));
  }
}

function reveal(): void {
  if (!shown?.card || revealed) return;
  revealed = true;
  byId("jp-back").hidden = false;
  byId("jp-reveal").hidden = true;
  byId("jp-grades").hidden = false;
}

async function grade(value: StudyGrade): Promise<void> {
  const card = shown?.card;
  if (!card || !revealed || busy) return;
  busy = true;
  buzz(value === "again" ? 30 : 10);
  try {
    revealed = false;
    paint(await call<StudySnapshot>(CHANNELS.japaneseAnswer, card.id, value));
  } catch (error) {
    revealed = true;
    flashToast(error instanceof Error ? error.message : t("could not save that answer"));
  } finally {
    busy = false;
  }
}

async function saveSettings(changes: { newPerDay?: number; decks?: StudyDeck[]; language?: string }): Promise<void> {
  try {
    revealed = false;
    paint(await call<StudySnapshot>(CHANNELS.japaneseSettings, changes));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save the deck setup"));
  }
}

function toggleDeck(deck: StudyDeck, on: boolean): Promise<void> {
  const current = shown?.settings.decks || [];
  const decks = on ? [...current, deck] : current.filter((item) => item !== deck);
  return saveSettings({ decks });
}

export function setupJapaneseView(): void {
  byId("jp-reveal").addEventListener("click", reveal);
  byId("jp-card").addEventListener("click", reveal);
  byId("jp-grades").addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("[data-grade]");
    if (button) void grade(button.dataset.grade as StudyGrade);
  });
  byId("jp-new-per-day").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLButtonElement>("[data-value]");
    if (option) void saveSettings({ newPerDay: Number(option.dataset.value) });
  });
  window.addEventListener("keydown", (event) => {
    if (!isViewActive("japanese") || !shown?.card) return;
    if ((event.target as Element)?.closest?.("input, textarea, select")) return;
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      if (!revealed) reveal();
      else void grade("good");
      return;
    }
    const index = ["1", "2", "3", "4"].indexOf(event.key);
    if (index >= 0) void grade(GRADES[index]);
  });
  listen<StudySnapshot>(CHANNELS.japaneseUpdated, (snapshot) => {
    if (isViewActive("japanese") && !busy) paint(snapshot);
  });
  onViewShown("japanese", () => void refresh());
  onViewHidden("japanese", () => window.clearTimeout(waitTimer));
}
